import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import Select from "react-select";
import usersService from "../services/usersService";
import { useTranslation } from "../i18n";

// Default filter state: empty = "All" for each section.
export const DEFAULT_FILTERS = {
  typeIds: [],
  teamIds: [],
  users: [], // react-select options [{ value: id, label: name }]
  itemKinds: [], // 'recognition' | 'nomination'
  points: [], // 'with' | 'without'
  involvement: [], // 'sent' | 'received' | 'mentioned'
  datePreset: null, // '7d' | '30d' | '90d' | 'year'
};

// Option labels are catalog keys, resolved with t(labelKey) inside components.
const ITEM_KIND_OPTIONS = [
  { value: "recognition", labelKey: "filters.kind_recognitions" },
  { value: "nomination", labelKey: "filters.kind_nominations" },
];

const POINTS_OPTIONS = [
  { value: "with", labelKey: "filters.with_points" },
  { value: "without", labelKey: "filters.without_points" },
];

const INVOLVEMENT_OPTIONS = [
  { value: "sent", labelKey: "filters.sent_by_me" },
  { value: "received", labelKey: "filters.received_by_me" },
  { value: "mentioned", labelKey: "filters.mentions_me" },
];

const DATE_PRESETS = [
  { value: "7d", labelKey: "filters.last_7_days" },
  { value: "30d", labelKey: "filters.last_30_days" },
  { value: "90d", labelKey: "filters.last_90_days" },
  { value: "year", labelKey: "filters.this_year" },
];

// Number of sections deviating from their default ("All") state.
// Two-option sections (item kind, points) with both boxes checked are
// equivalent to "All" and count as inactive.
export const countActiveSections = (filters) => {
  let count = 0;
  if (filters.typeIds.length > 0) count += 1;
  if (filters.teamIds.length > 0) count += 1;
  if (filters.users.length > 0) count += 1;
  if (filters.itemKinds.length === 1) count += 1;
  if (filters.points.length === 1) count += 1;
  if (filters.involvement.length > 0) count += 1;
  if (filters.datePreset) count += 1;
  return count;
};

// ISO timestamp for the start of the given preset range, or null.
export const dateFromForPreset = (preset) => {
  if (!preset) return null;
  if (preset === "year") {
    return new Date(new Date().getFullYear(), 0, 1).toISOString();
  }
  const days = { "7d": 7, "30d": 30, "90d": 90 }[preset];
  if (!days) return null;
  return new Date(Date.now() - days * 86400000).toISOString();
};

const toggleValue = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

// Recognition type icons are either an emoji or a custom-icon URL.
const isIconUrl = (icon) =>
  typeof icon === "string" &&
  (icon.startsWith("/api/v1/recognition-type-icons/") || icon.startsWith("http"));

const TypeIcon = ({ icon }) => {
  if (!icon) return null;
  if (isIconUrl(icon)) {
    return <img src={icon} alt="" className="w-4 h-4 object-contain flex-shrink-0" />;
  }
  return <span className="text-sm leading-none flex-shrink-0">{icon}</span>;
};

// Pill toggle chip: the core selection control (Airbnb/Linear-style).
const FilterChip = ({ selected, onClick, icon = null, children }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 max-w-full rounded-full border px-3 py-1.5 text-sm select-none transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 ${
      selected
        ? "org-bg-primary bg-purple-600 border-transparent text-white font-medium shadow-sm"
        : "bg-white border-gray-200 text-gray-700 hover:border-gray-400 hover:text-gray-900 active:scale-[0.97]"
    }`}
  >
    {selected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
    {icon}
    <span className="truncate">{children}</span>
  </button>
);

const COLLAPSE_AFTER = 8;

// Wrapping chip row; long lists collapse behind a "+N more" chip.
// Selected items past the fold stay visible while collapsed.
const ChipGroup = ({ items, isSelected, onToggle, renderIcon, getLabel, getKey }) => {
  const { t } = useTranslation("feed");
  const [expanded, setExpanded] = useState(false);
  const overflow = items.length - COLLAPSE_AFTER;
  const visible =
    expanded || overflow <= 0
      ? items
      : [
          ...items.slice(0, COLLAPSE_AFTER),
          ...items.slice(COLLAPSE_AFTER).filter(isSelected),
        ];
  const hiddenCount = items.length - visible.length;

  return (
    <div className="flex flex-wrap gap-2">
      {visible.map((item) => (
        <FilterChip
          key={getKey(item)}
          selected={isSelected(item)}
          onClick={() => onToggle(item)}
          icon={renderIcon ? renderIcon(item) : null}
        >
          {getLabel(item)}
        </FilterChip>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-colors"
        >
          {expanded ? t("filters.show_less") : t("filters.show_more", { count: hiddenCount })}
        </button>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
      {title}
    </p>
    {children}
  </div>
);

// Shared section list rendered by both the desktop panel and the mobile sheet.
const FilterSections = ({ filters, onChange, recognitionTypes, teams }) => {
  const { t } = useTranslation("feed");
  const [personOptions, setPersonOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    usersService
      .getColleagues(0, 100)
      .then((res) => {
        if (cancelled) return;
        const options = (res?.data || []).map((u) => ({
          value: u.id,
          label: u.full_name || u.email,
        }));
        setPersonOptions(options);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePersonSearch = (input) => {
    if (!input || input.length < 2) return;
    usersService
      .getColleagues(0, 100, input)
      .then((res) => {
        const found = (res?.data || []).map((u) => ({
          value: u.id,
          label: u.full_name || u.email,
        }));
        setPersonOptions((prev) => {
          const known = new Set(prev.map((o) => o.value));
          return [...prev, ...found.filter((o) => !known.has(o.value))];
        });
      })
      .catch(() => {});
  };

  const set = (key, value) => onChange({ ...filters, [key]: value });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        <Section title={t("filters.section_show")}>
          <div className="flex flex-wrap gap-2">
            {ITEM_KIND_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                selected={filters.itemKinds.includes(opt.value)}
                onClick={() => set("itemKinds", toggleValue(filters.itemKinds, opt.value))}
              >
                {t(opt.labelKey)}
              </FilterChip>
            ))}
          </div>
        </Section>

        <Section title={t("filters.section_points")}>
          <div className="flex flex-wrap gap-2">
            {POINTS_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                selected={filters.points.includes(opt.value)}
                onClick={() => set("points", toggleValue(filters.points, opt.value))}
              >
                {t(opt.labelKey)}
              </FilterChip>
            ))}
          </div>
        </Section>
      </div>

      {recognitionTypes.length > 0 && (
        <Section title={t("filters.section_type")}>
          <ChipGroup
            items={recognitionTypes}
            getKey={(type) => type.id}
            getLabel={(type) => type.name}
            renderIcon={(type) => <TypeIcon icon={type.icon} />}
            isSelected={(type) => filters.typeIds.includes(type.id)}
            onToggle={(type) => set("typeIds", toggleValue(filters.typeIds, type.id))}
          />
        </Section>
      )}

      {teams.length > 0 && (
        <Section title={t("filters.section_team")}>
          <ChipGroup
            items={teams}
            getKey={(team) => team.id}
            getLabel={(team) => team.name}
            isSelected={(team) => filters.teamIds.includes(team.id)}
            onToggle={(team) => set("teamIds", toggleValue(filters.teamIds, team.id))}
          />
        </Section>
      )}

      <Section title={t("filters.section_people")}>
        <Select
          isMulti
          value={filters.users}
          onChange={(selected) => set("users", selected || [])}
          onInputChange={handlePersonSearch}
          options={personOptions}
          placeholder={t("filters.search_people")}
          menuPosition="fixed"
          menuPlacement="auto"
          classNamePrefix="feed-filter-person"
          styles={{
            control: (base) => ({ ...base, borderRadius: 12, minHeight: 38, borderColor: "#e5e7eb" }),
            menu: (base) => ({ ...base, zIndex: 60 }),
          }}
        />
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        <Section title={t("filters.section_activity")}>
          <div className="flex flex-wrap gap-2">
            {INVOLVEMENT_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                selected={filters.involvement.includes(opt.value)}
                onClick={() =>
                  set("involvement", toggleValue(filters.involvement, opt.value))
                }
              >
                {t(opt.labelKey)}
              </FilterChip>
            ))}
          </div>
        </Section>

        <Section title={t("filters.section_date")}>
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((opt) => (
              <FilterChip
                key={opt.value}
                selected={filters.datePreset === opt.value}
                onClick={() =>
                  set("datePreset", filters.datePreset === opt.value ? null : opt.value)
                }
              >
                {t(opt.labelKey)}
              </FilterChip>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

// Flat list of every active filter value with a remover, for the
// summary chip row shown under the feed toolbar.
const activeFilterEntries = (filters, recognitionTypes, teams, t) => {
  const entries = [];
  const optionLabel = (options, value) => {
    const key = options.find((o) => o.value === value)?.labelKey;
    return key ? t(key) : undefined;
  };
  filters.itemKinds.length === 1 &&
    entries.push({
      key: `kind-${filters.itemKinds[0]}`,
      label: optionLabel(ITEM_KIND_OPTIONS, filters.itemKinds[0]),
      remove: (f) => ({ ...f, itemKinds: [] }),
    });
  filters.points.length === 1 &&
    entries.push({
      key: `points-${filters.points[0]}`,
      label: optionLabel(POINTS_OPTIONS, filters.points[0]),
      remove: (f) => ({ ...f, points: [] }),
    });
  filters.typeIds.forEach((id) => {
    const type = recognitionTypes.find((rt) => rt.id === id);
    entries.push({
      key: `type-${id}`,
      label: type?.name || t("filters.fallback_type"),
      icon: type ? <TypeIcon icon={type.icon} /> : null,
      remove: (f) => ({ ...f, typeIds: f.typeIds.filter((v) => v !== id) }),
    });
  });
  filters.teamIds.forEach((id) => {
    entries.push({
      key: `team-${id}`,
      label: teams.find((tm) => tm.id === id)?.name || t("filters.fallback_team"),
      remove: (f) => ({ ...f, teamIds: f.teamIds.filter((v) => v !== id) }),
    });
  });
  filters.users.forEach((u) => {
    entries.push({
      key: `user-${u.value}`,
      label: u.label,
      remove: (f) => ({ ...f, users: f.users.filter((o) => o.value !== u.value) }),
    });
  });
  filters.involvement.forEach((v) => {
    entries.push({
      key: `inv-${v}`,
      label: optionLabel(INVOLVEMENT_OPTIONS, v),
      remove: (f) => ({ ...f, involvement: f.involvement.filter((x) => x !== v) }),
    });
  });
  if (filters.datePreset) {
    entries.push({
      key: `date-${filters.datePreset}`,
      label: optionLabel(DATE_PRESETS, filters.datePreset),
      remove: (f) => ({ ...f, datePreset: null }),
    });
  }
  return entries;
};

/**
 * Removable summary chips for every active filter value, shown under the
 * feed toolbar while the panel is closed. Each chip clears its own value;
 * "Clear all" resets everything.
 */
export const ActiveFilterChips = ({ filters, onChange, onClear, recognitionTypes = [], teams = [] }) => {
  const { t } = useTranslation("feed");
  const entries = activeFilterEntries(filters, recognitionTypes, teams, t);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3">
      {entries.map((entry) => (
        <span
          key={entry.key}
          className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-purple-50 text-purple-700 border border-purple-100 pl-3 pr-1.5 py-1 text-sm"
        >
          {entry.icon}
          <span className="truncate">{entry.label}</span>
          <button
            type="button"
            onClick={() => onChange(entry.remove(filters))}
            className="p-0.5 rounded-full hover:bg-purple-100 text-purple-500 hover:text-purple-800 transition-colors"
            aria-label={t("filters.remove_filter", { label: entry.label })}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}
      {entries.length > 1 && (
        <button
          type="button"
          onClick={onClear}
          className="text-sm font-medium text-gray-500 hover:text-gray-800 px-1.5 py-1 transition-colors"
        >
          {t("filters.clear_all")}
        </button>
      )}
    </div>
  );
};

/**
 * Feed filter panel. Desktop: inline panel that expands below the toolbar.
 * Mobile: bottom sheet portaled to body.
 */
export default function FeedFilterPanel({
  open,
  onClose,
  isMobile,
  filters,
  onChange,
  onClear,
  recognitionTypes = [],
  teams = [],
  resultCount = null,
  loading = false,
}) {
  const { t } = useTranslation("feed");
  // Keep the mobile sheet mounted while it animates out.
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      // Next frame so the enter transition runs from translate-y-full.
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const timeout = setTimeout(() => setRendered(false), 300);
    return () => clearTimeout(timeout);
  }, [open]);

  const hasActive = countActiveSections(filters) > 0;

  if (!isMobile) {
    return (
      <AnimatePresence initial={false}>
        {/* Card styling sits on the height-animated element itself so its
            shadow is not clipped by overflow:hidden; the toolbar gap
            animates as margin instead of inner padding. */}
        {open && (
          <motion.div
            key="feed-filters"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 12 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
            className="bg-white shadow-card rounded-2xl border border-gray-100"
          >
            <div className="p-5">
              <FilterSections
                filters={filters}
                onChange={onChange}
                recognitionTypes={recognitionTypes}
                teams={teams}
              />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <button
                type="button"
                onClick={onClear}
                disabled={!hasActive}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors disabled:text-gray-300"
              >
                {t("filters.clear_all")}
              </button>
              <div className="flex items-center gap-3">
                {resultCount !== null && (
                  <span className={`text-sm text-gray-400 transition-opacity ${loading ? "opacity-50" : ""}`}>
                    {t("filters.results", { count: resultCount })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                >
                  {t("filters.done")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (!rendered) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl max-h-[80vh] flex flex-col transition-transform duration-300 ease-out ${
          visible ? "translate-y-0" : "translate-y-full"
        }`}
        role="dialog"
        aria-label={t("filters.dialog_label")}
      >
        <div className="flex-shrink-0 bg-white rounded-t-2xl px-5 pt-2.5 pb-3 border-b border-gray-100">
          <div className="mx-auto mb-2.5 h-1 w-9 rounded-full bg-gray-300" />
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">{t("filters.title")}</h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label={t("filters.close")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto overscroll-contain px-5 py-4 flex-1">
          <FilterSections
            filters={filters}
            onChange={onChange}
            recognitionTypes={recognitionTypes}
            teams={teams}
          />
        </div>
        <div className="flex-shrink-0 bg-white flex items-center justify-between px-5 py-3 border-t border-gray-100 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClear}
            disabled={!hasActive}
            className="text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors disabled:text-gray-300"
          >
            {t("filters.clear_all")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 org-bg-primary bg-purple-600 text-white text-sm font-medium rounded-full transition-colors"
          >
            {resultCount !== null && !loading
              ? t("filters.show_results", { count: resultCount })
              : t("filters.done")}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
